// JointForm — механическое соединение между двумя ТЕЛАМИ.
//
// Компонентов (как в Fusion) у нас нет и пока не планируется: для
// робототехники достаточно связать две детали и посмотреть, как ходит
// механизм. Компонент со своей историей здесь чаще мешает, чем помогает.
//
// Три типа, которых хватает на большинство механизмов:
//   • шарнир — вращение вокруг оси (петля, колесо, вал);
//   • ползун — движение вдоль оси (каретка, поршень);
//   • жёсткое — детали скреплены намертво.
//
// Ползунок положения двигает деталь на живой сцене: видно ход механизма,
// не собирая его в железе.
import React from 'react';
import useCadStore from '../store/useCadStore';
import { getGeometryClient } from '../geometryClient';
import RU from '../i18n/ru';
import ExprInput from './ExprInput';
import cl from './Properties.module.css';

/** Центр габаритного ящика тела — точка соединения по умолчанию. */
function bodyCenter(mesh) {
    const p = mesh?.positions;
    if (!p || p.length < 3) return [0, 0, 0];
    let mnX = Infinity, mnY = Infinity, mnZ = Infinity;
    let mxX = -Infinity, mxY = -Infinity, mxZ = -Infinity;
    for (let i = 0; i < p.length; i += 3) {
        if (p[i] < mnX) mnX = p[i];
        if (p[i] > mxX) mxX = p[i];
        if (p[i + 1] < mnY) mnY = p[i + 1];
        if (p[i + 1] > mxY) mxY = p[i + 1];
        if (p[i + 2] < mnZ) mnZ = p[i + 2];
        if (p[i + 2] > mxZ) mxZ = p[i + 2];
    }
    return [(mnX + mxX) / 2, (mnY + mxY) / 2, (mnZ + mxZ) / 2];
}

const FormRow = ({ label, children }) => (
    <div className={cl.formRow}>
        <div className={cl.formLabel}>{label}</div>
        <div className={cl.formField}>{children}</div>
    </div>
);

export default function JointForm() {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    const addJoint = useCadStore((s) => s.addJoint);

    const jointKind = pendingFeature?.jointKind || 'revolute';
    const bodyA = pendingFeature?.bodyA || null;
    const bodyB = pendingFeature?.bodyB || null;
    const value = pendingFeature?.value ?? 0;

    /*
     * Клик по телу на сцене заполняет сначала основание, потом подвижную
     * деталь. Порядок важен: основание остаётся на месте, движется вторая.
     */
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'joint') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const picked = selection.ids[0];
        if (!bodyA) {
            updatePendingFeature({ bodyA: picked });
            return;
        }
        if (!bodyB && picked !== bodyA) {
            updatePendingFeature({
                bodyB: picked,
                origin: bodyCenter(bodyMeshes.get(picked)),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    /*
     * Живой показ хода. Запрос к ядру не ставим в очередь: пока считается
     * один, новые значения ползунка пропускаем — иначе на быстром движении
     * копится хвост из десятков пересчётов, и деталь дёргается с задержкой.
     */
    const inflightRef = React.useRef(false);
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'joint') return;
        if (!bodyB || jointKind === 'rigid') return;
        if (inflightRef.current) return;
        inflightRef.current = true;
        (async () => {
            try {
                await getGeometryClient().applyJoint({
                    bodyId: bodyB,
                    kind: jointKind,
                    value,
                    origin: pendingFeature.origin || [0, 0, 0],
                    axis: pendingFeature.axis || [0, 0, 1],
                });
            } catch (e) {
                console.warn('[JointForm] показ не удался:', e?.message || e);
            } finally {
                inflightRef.current = false;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, jointKind, bodyB]);

    if (!pendingFeature || pendingFeature.kind !== 'joint') return null;

    const axis = pendingFeature.axis || [0, 0, 1];
    const axisName = axis[0] ? 'X' : axis[1] ? 'Y' : 'Z';
    const setAxis = (name) => updatePendingFeature({
        axis: name === 'X' ? [1, 0, 0] : name === 'Y' ? [0, 1, 0] : [0, 0, 1],
    });

    const isSlider = jointKind === 'slider';
    const unit = isSlider ? 'мм' : '°';
    const range = isSlider ? 100 : 180;

    /*
     * Пределы хода. Заданный предел сужает ползунок, незаданный оставляет
     * ход по умолчанию — так ползунок всегда охватывает ровно то, что
     * механизм реально может.
     */
    const limMin = (typeof pendingFeature.min === 'number') ? pendingFeature.min : null;
    const limMax = (typeof pendingFeature.max === 'number') ? pendingFeature.max : null;
    const hasLimits = limMin !== null || limMax !== null;
    const sliderMin = limMin !== null ? limMin : -range;
    const sliderMax = limMax !== null ? limMax : range;
    const clampToLimits = (v) => {
        let out = v;
        if (limMin !== null && out < limMin) out = limMin;
        if (limMax !== null && out > limMax) out = limMax;
        return out;
    };

    const onOk = () => {
        if (!bodyA || !bodyB) return;
        addJoint({
            id: `joint_${Date.now().toString(36)}`,
            kind: jointKind,
            bodyA,
            bodyB,
            origin: pendingFeature.origin || [0, 0, 0],
            axis,
            value: clampToLimits(value),
            min: limMin,
            max: limMax,
        });
        setPendingFeature(null);
    };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Соединение</div>

            <FormRow label="Тип">
                <select
                    className={cl.select}
                    value={jointKind}
                    onChange={(e) => updatePendingFeature({
                        jointKind: e.target.value,
                        value: 0,
                    })}
                >
                    <option value="revolute">Шарнир — вращение вокруг оси</option>
                    <option value="slider">Ползун — движение вдоль оси</option>
                    <option value="rigid">Жёсткое — детали скреплены</option>
                </select>
            </FormRow>

            <FormRow label="Основание">
                {bodyA ? (
                    <span className={cl.profileBadge}>{bodyA}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по неподвижной детали
                    </span>
                )}
            </FormRow>

            <FormRow label="Подвижная">
                {bodyB ? (
                    <span className={cl.profileBadge}>{bodyB}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по второй детали
                    </span>
                )}
            </FormRow>

            {jointKind !== 'rigid' && (
                <>
                    <FormRow label="Ось">
                        <select
                            className={cl.select}
                            value={axisName}
                            onChange={(e) => setAxis(e.target.value)}
                        >
                            <option value="X">X</option>
                            <option value="Y">Y</option>
                            <option value="Z">Z</option>
                        </select>
                    </FormRow>

                    <FormRow label={isSlider ? 'Смещение' : 'Угол'}>
                        <ExprInput
                            value={value}
                            unit={unit}
                            onChange={(v) => updatePendingFeature({ value: v })}
                        />
                    </FormRow>

                    {/*
                        Пределы хода. Без них деталь крутится на любой угол,
                        включая невозможный: сервопривод ходит 0…180°,
                        каретка упирается в концевик. Проверять механизм без
                        пределов бессмысленно — он «работает» и там, где в
                        железе упрётся.

                        Пустое поле = без ограничения.
                    */}
                    <FormRow label="От">
                        <ExprInput
                            value={limMin ?? ''}
                            unit={unit}
                            placeholder="без предела"
                            allowEmpty
                            onChange={(v) => updatePendingFeature({ min: v })}
                        />
                    </FormRow>
                    <FormRow label="До">
                        <ExprInput
                            value={limMax ?? ''}
                            unit={unit}
                            placeholder="без предела"
                            allowEmpty
                            onChange={(v) => updatePendingFeature({ max: v })}
                        />
                    </FormRow>

                    {/* Ползунок — чтобы «покрутить» механизм и увидеть ход. */}
                    <input
                        type="range"
                        min={sliderMin}
                        max={sliderMax}
                        step={isSlider ? 1 : 5}
                        value={value}
                        disabled={!bodyB}
                        onChange={(e) => updatePendingFeature({
                            value: clampToLimits(parseFloat(e.target.value)),
                        })}
                        style={{ width: '100%', marginTop: 6 }}
                    />
                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                        {hasLimits
                            ? `Ход ограничен: ${sliderMin}…${sliderMax} ${unit}`
                            : 'Тяни ползунок — деталь двигается на сцене'}
                    </div>
                </>
            )}

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!bodyA || !bodyB}
                >
                    OK
                </button>
                <button
                    className={`${cl.btn} ${cl.btnSecondary}`}
                    onClick={() => setPendingFeature(null)}
                >
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
}
